import { LitElement, PropertyValueMap, html } from 'lit';
import { customElement, state, property } from 'lit/decorators.js';

import { emit } from '@amityeko/pbc-core/dist/utilities/event';
import { withAuth } from '@amityeko/pbc-core/dist/providers/auth/consumer-mixin';

import messageIcon from '@amityeko/design-system/dist/assets/icons/message';

import '@amityeko/design-system/dist/elements/typography/tg-title';
import '@amityeko/design-system/dist/elements/typography/tg-body-two';
import '@amityeko/design-system/dist/elements/icon';
import '@amityeko/design-system/dist/elements/badge';
import '@amityeko/design-system/dist/elements/avatar';
import '@amityeko/design-system/dist/elements/button';
import '@amityeko/design-system/dist/elements/skeleton';

import type { Recognition } from './types';

import styles from './styles';

/**
 * @event rnr-select-view-recognition {{ Recognition }} - Emitted when the "View" button is clicked.
 */
@customElement('rnr-recognition-compact-preview')
export default class RnRRecognitionCompactPreview extends withAuth(LitElement) {
  static styles = styles;

  @property()
  recognitionId?: string;

  @property({ type: Boolean })
  shouldUsePronoun: boolean = false;

  @state()
  recognition?: Recognition;

  @state()
  badgeImage?: string;

  @state()
  isLoading: boolean = true;

  protected updated(changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>) {
    if (!changedProperties.has('auth') || !this.auth) return;

    const prevAuth: typeof this.auth = changedProperties.get('auth');
    if (this.auth.isAuthenticated && !prevAuth?.isAuthenticated) {
      this._getRecognition();
    }
  }

  private async _getRecognition() {
    if (!this.auth?.http) return;

    try {
      this.recognition = await this.auth.http.get(`/api/v1/recognitions/${this.recognitionId}`);
      this.badgeImage = await this.auth.http.media(
        `/api/v1/media/images/${this.recognition?.badge?.thumbnail}`,
      );
    } catch (e: any) {
      emit(this, 'amityeko-notification--toast', {
        detail: { message: e?.error?.message, variant: 'error' },
      });
    } finally {
      this.isLoading = false;
    }
  }

  private _onClickView() {
    emit(this, 'rnr-select-view-recognition', { detail: { ...this.recognition } });
  }

  protected render() {
    const senderName = this.recognition?.sender?.displayName;
    const senderId = this.recognition?.sender?.id;
    const receiverName = this.recognition?.receiver?.displayName;
    const receiverId = this.recognition?.receiver?.id;

    const congratMessage =
      this.auth?.userId === senderId
        ? `${this.shouldUsePronoun ? 'You' : senderName || senderId} sent a recognition to ${
            receiverName || receiverId
          }.`
        : `${senderName || senderId} sent a recognition to ${
            this.shouldUsePronoun ? 'you' : receiverName || receiverId
          }.`;

    return html`
      ${this.isLoading
        ? html`
            <div class="recognition-compact-preview">
              <amityeko-skeleton
                .shape=${'circle'}
                .width=${'60px'}
                .height=${'60px'}
              ></amityeko-skeleton>
              <amityeko-skeleton .width=${'124px'} .height=${'24px'}></amityeko-skeleton>
              <div class="content">
                <amityeko-skeleton .width=${'264px'} .height=${'16px'}></amityeko-skeleton>
                <amityeko-skeleton .width=${'200px'} .height=${'16px'}></amityeko-skeleton>
              </div>
            </div>
          `
        : html`
            <div class="recognition-compact-preview">
              <amityeko-avatar
                shape="circle"
                .avatarSize=${'60px'}
                .image=${this.badgeImage || ''}
                .initials=${'R'}
              ></amityeko-avatar>
              <amityeko-tg-title>${this.recognition?.badge?.name}</amityeko-tg-title>
              <amityeko-tg-body-two> ${congratMessage} </amityeko-tg-body-two>
              <amityeko-badge variant="primary">
                ${this.recognition?.rewards[0].value} points
              </amityeko-badge>
              <amityeko-button variant="primary" ?hasPrefix=${true} @click=${this._onClickView}>
                <amityeko-icon slot="prefix">${messageIcon}</amityeko-icon>
                ${this.recognition?.type === 'video' ? 'View video' : 'View message'}
              </amityeko-button>
            </div>
          `}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'rnr-recognition-compact-preview': RnRRecognitionCompactPreview;
  }
}
